import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, Row, Col, Select, Tooltip } from 'antd'
import styled from 'styled-components'
import { IconClose } from '@edulastic/icons'
import { accessibilityColors } from '@edulastic/colors'
import { uniqBy } from 'lodash'
import {
  notification,
  FieldLabel,
  TextInputStyled,
  TextAreaInputStyled,
} from '@edulastic/common'
import { tagsApi } from '@edulastic/api'
import { StrippedSelectInputStyled } from '@edulastic/common/src/components/InputStyles'
import { Container } from './styled/Container'
import { ItemBody } from './styled/ItemBody'
import { selectsData } from '../../../author/TestPage/components/common'
import { SelectSuffixIcon } from './styled/SelectSuffixIcon'
import RecentCollectionsList from './RecentCollectionsList'
import { setSelectSearchAriaAttributes } from '../../../common/utils/helpers'

const bloomsTaxonomyOptions = [
  'Remember',
  'Understand',
  'Apply',
  'Analyze',
  'Evaluate',
  'Create',
]

const SecondBlock = ({
  t,
  onChangeTags,
  onQuestionDataSelect,
  onChangeExternalData,
  depthOfKnowledge = '',
  authorDifficulty = '',
  bloomsTaxonomy = '',
  testletQuestionId = '',
  testletResponseIds = '',
  testletAdditionalMetadata = '',
  tags = [],
  allTagsData,
  addNewTag,
  handleCollectionsSelect,
  handleRecentCollectionsSelect,
  collections,
  highlightCollection,
  recentCollectionsList,
  collectionsToShow,
  showAdditionalMeta,
  isDerivedFromPremiumBank = false,
}) => {
  const newAllTagsData = uniqBy([...allTagsData, ...tags], 'tagName')
  const [searchValue, setSearchValue] = useState('')

  // Remove aria-label from icon elements in tags-select dropdown options
  const removeAriaLabelFromTagsSelectIcons = () => {
    // Find all li elements with data-cy starting with "tags-select-"
    const optionElements = document.querySelectorAll(
      'li[role="option"][data-cy^="tags-select-"]'
    )
    optionElements.forEach((option) => {
      // Find icon elements within each option
      const iconElements = option.querySelectorAll('i[aria-label]')
      iconElements.forEach((icon) => {
        icon.removeAttribute('aria-label')
      })
    })
  }

  const selectTags = async (id) => {
    let newTag = {}
    if (id === searchValue) {
      const tempSearchValue = searchValue
      setSearchValue('')
      try {
        const { _id, tagName } = await tagsApi.create({
          tagName: tempSearchValue,
          tagType: 'testitem',
        })
        newTag = { _id, tagName }
        addNewTag({ tag: newTag, tagType: 'testitem' })
      } catch (e) {
        notification({ messageKey: 'savingTagErr' })
      }
    } else {
      newTag = newAllTagsData.find((tag) => tag._id === id)
    }
    const newTags = [...tags, newTag]
    onChangeTags(newTags)
    setSearchValue('')
    // Remove aria-label from icons after tag is selected
    setTimeout(() => {
      removeAriaLabelFromTagsSelectIcons()
    }, 0)
  }

  const deselectTags = (id) => {
    const newTags = tags.filter((tag) => tag._id !== id)
    onChangeTags(newTags)
    // Remove aria-label from icons after tag is deselected
    setTimeout(() => {
      removeAriaLabelFromTagsSelectIcons()
    }, 0)
  }

  const searchTags = async (value) => {
    if (
      newAllTagsData.some(
        (tag) => tag.tagName.toLowerCase() === value.toLowerCase()
      )
    ) {
      setSearchValue('')
    } else {
      setSearchValue(value)
    }
  }

  // here we are filtering out the collection which are not from current district.
  const filteredCollections = useMemo(
    () =>
      collections.filter((c) => collectionsToShow.some((o) => o._id === c._id)),
    [collections, collectionsToShow]
  )

  const allowedCollectionIds = useMemo(
    () => collectionsToShow.map(({ _id }) => _id),
    [collectionsToShow]
  )

  const recentCollectionsListAccessible = useMemo(() =>
    recentCollectionsList.filter((r) => allowedCollectionIds.includes(r._id), [
      recentCollectionsList,
      allowedCollectionIds,
    ])
  )

  return (
    <Container padding="20px">
      <Row gutter={24}>
        <Col md={6}>
          <ItemBody>
            <FieldLabel>{t('component.options.depthOfKnowledge')}</FieldLabel>
            <StrippedSelectInputStyled
              bg="white"
              data-cy="dokSelect"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              placeholder={t('component.options.selectDOK')}
              onSelect={onQuestionDataSelect('depthOfKnowledge')}
              value={depthOfKnowledge}
              suffixIcon={<SelectSuffixIcon type="caret-down" />}
            >
              <Select.Option key="Select DOK" value="">
                Select DOK
              </Select.Option>
              {selectsData.allDepthOfKnowledge.map(
                (el) =>
                  el.value && (
                    <Select.Option
                      data-cy={`dok-select-${el.text}`}
                      key={el.value}
                      value={el.value}
                    >
                      {el.text}
                    </Select.Option>
                  )
              )}
            </StrippedSelectInputStyled>
          </ItemBody>
          <ItemBody>
            <FieldLabel htmlFor="externalQuestionId">
              {t('component.options.externalQuestionId')}
            </FieldLabel>
            <TextInputStyled
              id="externalQuestionId"
              data-cy="externalQuestionId"
              value={testletQuestionId}
              onChange={onChangeExternalData('testletQuestionId')}
            />
          </ItemBody>
        </Col>
        <Col md={6}>
          <ItemBody>
            <FieldLabel>{t('component.options.difficultyLevel')}</FieldLabel>
            <StrippedSelectInputStyled
              bg="white"
              data-cy="difficultySelect"
              placeholder={t('component.options.selectDifficulty')}
              onSelect={onQuestionDataSelect('authorDifficulty')}
              value={authorDifficulty}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              suffixIcon={<SelectSuffixIcon type="caret-down" />}
            >
              <Select.Option key="Select Difficulty Level" value="">
                Select Difficulty Level
              </Select.Option>
              {selectsData.allAuthorDifficulty.map(
                (el) =>
                  el.value && (
                    <Select.Option
                      data-cy={`difficulty-select-${el.text}`}
                      key={el.value}
                      value={el.value}
                    >
                      {el.text}
                    </Select.Option>
                  )
              )}
            </StrippedSelectInputStyled>
          </ItemBody>
          <ItemBody>
            <FieldLabel htmlFor="externalResponseIds">
              {t('component.options.externalResponseIds')}
            </FieldLabel>
            <TextInputStyled
              id="externalResponseIds"
              data-cy="externalResponseIds"
              value={testletResponseIds}
              maxLength={250}
              onChange={onChangeExternalData('testletResponseIds')}
            />
          </ItemBody>
        </Col>
        <Col md={6}>
          <ItemBody>
            <FieldLabel>{t('component.options.blooomTaxonomy')}</FieldLabel>
            <StrippedSelectInputStyled
              data-cy="bloomsTaxonomy"
              bg="white"
              placeholder={t('component.options.blooomTaxonomy')}
              onSelect={onQuestionDataSelect('bloomsTaxonomy')}
              value={bloomsTaxonomy}
              dropdownClassName="custom-antd-select"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              suffixIcon={<SelectSuffixIcon type="caret-down" />}
            >
              <Select.Option key={"Select Bloom's Taxonomy"} value="">
                Select Bloom&apos;s Taxonomy
              </Select.Option>
              {bloomsTaxonomyOptions.map((x) => (
                <Select.Option key={x.toLowerCase()} value={x.toLowerCase()}>
                  {x}
                </Select.Option>
              ))}
            </StrippedSelectInputStyled>
          </ItemBody>
          {showAdditionalMeta && (
            <ItemBody>
              <FieldLabel htmlFor="additionalMetadata">
                {t('component.options.additionalMetadata')}
              </FieldLabel>
              <TextAreaInputStyled
                id="additionalMetadata"
                rows={4}
                padding="6px 15px"
                value={testletAdditionalMetadata}
                onChange={onChangeExternalData('testletAdditionalMetadata')}
              />
            </ItemBody>
          )}
        </Col>

        {(collectionsToShow.length > 0 ||
          recentCollectionsList?.length > 0) && (
          <Col md={6}>
            {collectionsToShow.length > 0 && (
              <ItemBody>
                <FieldLabel>Collections</FieldLabel>
                <Tooltip
                  title={
                    isDerivedFromPremiumBank &&
                    'Action not permitted on clone of a premium content'
                  }
                >
                  <StrippedSelectInputStyled
                    mode="multiple"
                    className="tagsSelect"
                    data-cy="collectionsSelect"
                    bg="white"
                    placeholder="Please select"
                    dropdownClassName="custom-antd-select"
                    value={filteredCollections.flatMap((c) => c.bucketIds)}
                    onChange={(value, options) =>
                      handleCollectionsSelect(value, options, collectionsToShow)
                    }
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    disabled={isDerivedFromPremiumBank}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    suffixIcon={<SelectSuffixIcon type="caret-down" />}
                    autoFocus={highlightCollection}
                  >
                    {collectionsToShow.map((o) => (
                      <Select.Option
                        key={o.bucketId}
                        value={o.bucketId}
                        _id={o._id}
                        type={o.type}
                        collectionName={o.collectionName}
                      >
                        {`${o.collectionName} - ${o.name}`}
                      </Select.Option>
                    ))}
                  </StrippedSelectInputStyled>
                </Tooltip>
              </ItemBody>
            )}
            {recentCollectionsListAccessible?.length > 0 && (
              <RecentCollectionsList
                recentCollectionsList={recentCollectionsListAccessible}
                collections={collections || []}
                handleCollectionsSelect={handleRecentCollectionsSelect}
                isDerivedFromPremiumBank={isDerivedFromPremiumBank}
              />
            )}
          </Col>
        )}
        <Col md={6}>
          <ItemBody>
            <FieldLabel id="tags-label">
              {t('component.options.tags')}
            </FieldLabel>
            <div id="tags-description" style={{ display: 'none' }}>
              Use arrow keys to navigate options, Enter to select, Escape to
              close dropdown
            </div>
            {searchValue.length && !searchValue.trim().length ? (
              <StrippedSelectInputStyled
                mode="multiple"
                bg="white"
                optionLabelProp="title"
                className="tagsSelect"
                placeholder="Please enter"
                filterOption={(input, option) =>
                  option.props.title
                    .toLowerCase()
                    .includes(input.trim().toLowerCase())
                }
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                removeIcon={
                  <StyledButton tabIndex={0} aria-label="Remove Tag">
                    <StyledIconClose aria-hidden="true" />
                  </StyledButton>
                }
                ref={(node) => {
                  setSelectSearchAriaAttributes(node, [
                    { name: 'aria-labelledby', value: 'tags-label' },
                    { name: 'aria-controls', value: 'tags-listbox' },
                    { name: 'aria-describedby', value: 'tags-description' },
                    { name: 'role', value: 'combobox' },
                  ])
                  // Remove aria attributes from the wrapper div element
                  if (node?.rcSelect?.selectionRef) {
                    const wrapperDiv = node.rcSelect.selectionRef
                    wrapperDiv.removeAttribute('role')
                    wrapperDiv.removeAttribute('aria-autocomplete')
                    wrapperDiv.removeAttribute('aria-haspopup')
                    wrapperDiv.removeAttribute('aria-controls')
                    wrapperDiv.removeAttribute('aria-expanded')
                  }
                }}
                onSearch={searchTags}
              >
                <Select.Option key={0} value="invalid" title="invalid" disabled>
                  Please enter valid characters
                </Select.Option>
              </StrippedSelectInputStyled>
            ) : (
              <StrippedSelectInputStyled
                data-cy="tagsSelect"
                mode="multiple"
                className="tagsSelect"
                bg="white"
                optionLabelProp="title"
                placeholder="Please enter"
                value={tags.map((x) => x._id)}
                onSearch={searchTags}
                onSelect={selectTags}
                onDeselect={deselectTags}
                dropdownClassName="custom-antd-select"
                filterOption={(input, option) =>
                  option.props.title.toLowerCase().includes(input.toLowerCase())
                }
                ref={(node) => {
                  setSelectSearchAriaAttributes(node, [
                    { name: 'aria-labelledby', value: 'tags-label' },
                    { name: 'aria-controls', value: 'tags-listbox' },
                    { name: 'aria-describedby', value: 'tags-description' },
                    { name: 'role', value: 'combobox' },
                  ])
                  // Remove aria attributes from the wrapper div element
                  if (node?.rcSelect?.selectionRef) {
                    const wrapperDiv = node.rcSelect.selectionRef
                    wrapperDiv.removeAttribute('role')
                    wrapperDiv.removeAttribute('aria-autocomplete')
                    wrapperDiv.removeAttribute('aria-haspopup')
                    wrapperDiv.removeAttribute('aria-controls')
                    wrapperDiv.removeAttribute('aria-expanded')
                  }
                  // Remove aria-label from icons after a short delay to ensure DOM is ready
                  setTimeout(() => {
                    removeAriaLabelFromTagsSelectIcons()
                  }, 0)
                }}
                onDropdownVisibleChange={(open) => {
                  if (open) {
                    // When dropdown opens, remove aria-label from icons after a short delay
                    setTimeout(() => {
                      removeAriaLabelFromTagsSelectIcons()
                    }, 100)
                  }
                }}
                removeIcon={
                  <StyledButton tabIndex={0} aria-label="Remove Tag">
                    <StyledIconClose aria-hidden="true" />
                  </StyledButton>
                }
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              >
                {searchValue.trim() ? (
                  <Select.Option
                    data-cy={`dok-select-${searchValue}`}
                    key={0}
                    value={searchValue}
                    title={searchValue}
                    role="option"
                    aria-label="Create New Tag"
                    aria-selected="false"
                  >
                    {`${searchValue} (Create New Tag)`}
                  </Select.Option>
                ) : (
                  ''
                )}
                {newAllTagsData.map(({ tagName, _id }) => (
                  <Select.Option
                    data-cy={`tags-select-${tagName}`}
                    key={_id}
                    value={_id}
                    title={tagName}
                    role="option"
                    aria-label={tagName}
                    aria-selected={tags.some((tag) => tag._id === _id)}
                  >
                    {tagName}
                  </Select.Option>
                ))}
              </StrippedSelectInputStyled>
            )}
          </ItemBody>
        </Col>
      </Row>
    </Container>
  )
}

SecondBlock.propTypes = {
  t: PropTypes.func.isRequired,
  onChangeTags: PropTypes.func.isRequired,
  onQuestionDataSelect: PropTypes.func.isRequired,
  depthOfKnowledge: PropTypes.string.isRequired,
  authorDifficulty: PropTypes.string.isRequired,
  tags: PropTypes.array,
}

SecondBlock.defaultProps = {
  tags: [],
}

export default SecondBlock

export const StyledButton = styled(Button)`
  width: fit-content;
  padding: 0px;
  height: 15px;
  background: none;
  outline: none;
  border: none; d
  margin-right: -3px;
  &:hover {
    background: none;
  }
  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blue};
    background: none;
  }
`

export const StyledIconClose = styled(IconClose)`
  width: 10px;
  height: 10px !important;
`
